import React, { useEffect, useMemo } from "react";
import { isEmpty, sortBy } from "lodash";
import locale from "@bonliva-crm/locale";
import { NodeType, useCallListSelector_GetListsLazyQuery } from "@graphql";
import { Option } from "@components/Select";
import { SearchSelect } from "@components/Select/SearchSelect";
import { Grid3 } from "../Grid3";

type ListItem = {
  _id: string;
  name: string;
  type: NodeType;
  nodes: Array<{ NodeType: NodeType }>;
};

function buildListOptions(
  rawLists: ListItem[] | undefined,
  nodeType: NodeType,
  groupLabel: string
): Option[] {
  if (!rawLists?.length) return [];

  const sorted = sortBy(rawLists, (x) => x.name?.toLocaleLowerCase?.() ?? "");
  const filtered = sorted.filter((x) => x.type === nodeType && !isEmpty(x.nodes));

  const options: Option[] = filtered.map((l) => {
    const total = l.nodes.filter((n) => n.NodeType === nodeType).length;
    return {
      description: locale.CallListCount(total),
      parent: groupLabel,
      label: l.name,
      value: l._id
    } as Option;
  });

  if (options.length) {
    options.unshift({
      description: null,
      parent: null,
      label: groupLabel,
      value: groupLabel,
      skipCheckbox: true,
      unselectable: true
    } as Option);
  }

  return options;
}

export function CallListSelector({
  nodeType,
  value,
  onChange,
  dirty
}: {
  nodeType: NodeType;
  value: string[];
  onChange: (v: string[]) => void;
  dirty: boolean;
}) {
  const [fetchLists, { data, loading, called }] = useCallListSelector_GetListsLazyQuery({
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    if (!called && !loading) {
      fetchLists();
    }
  }, []);

  const rawCallLists = data?.me?.CallLists as ListItem[] | undefined;
  const rawContactLists = data?.me?.ContactLists as ListItem[] | undefined;

  const callListOptions = useMemo(
    () => buildListOptions(rawCallLists, nodeType, locale.CallLists),
    [rawCallLists, nodeType]
  );

  const contactListOptions = useMemo(
    () => buildListOptions(rawContactLists, nodeType, locale.ContactLists),
    [rawContactLists, nodeType]
  );

  const listOptions = useMemo(
    () => [...callListOptions, ...contactListOptions],
    [callListOptions, contactListOptions]
  );

  const disabled = useMemo(
    () => !listOptions.length || loading,
    [listOptions.length, loading]
  );

  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        testName="callLists"
        value={value}
        onChange={onChange}
        label={`${locale.CallLists} & ${locale.ContactLists}`}
        options={listOptions}
        sortGroups={false}
        sortOptions={false}
        disabled={disabled}
      />
    </Grid3>
  );
}
